import { BaseChannel, User } from "@sendbird/chat"
import { GroupChannel, GroupChannelHandler } from "@sendbird/chat/groupChannel"
import {
  BaseMessage,
  FileMessage,
  ReplyType,
  UserMessage,
} from "@sendbird/chat/message"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import {
  connectToSendbird,
  disconnectFromSendbird,
  getGroupChannelList,
  getGroupChannel,
} from "./sendbird"
import sb from "./sendbird"
import { useSBCredentialsQuery } from "../../networking/user/queries"
import "./sendbird.scss"
interface SendbirdContextType {
  // Connection State
  isConnected: boolean
  currentUser: User | null

  // Channels
  channels: GroupChannel[]
  currentChannel: GroupChannel | null
  setCurrentChannel: (channel: GroupChannel | null) => void
  refreshChannels: () => Promise<void>

  // Messages
  messages: BaseMessage[]
  sendMessage: (
    channelUrl: string,
    message: string,
    parentMessageId?: number
  ) => Promise<UserMessage>
  sendFileMessage: (channelUrl: string, file: File) => Promise<FileMessage>
  deleteMessage: (channelUrl: string, message: BaseMessage) => Promise<void>
  updateMessage: (
    channelUrl: string,
    messageId: number,
    text: string
  ) => Promise<UserMessage>
  loadPreviousMessages: () => Promise<void>
  scrollToBottomOfMessages: () => void
  // Message Status
  markAsRead: (channelUrl: string) => Promise<void>

  // Loading States
  isLoadingMessages: boolean
  isSendingMessage: boolean

  hasMore: boolean
  setMessages: (messages: BaseMessage[]) => void

  getChannelByUrl: (channelUrl: string) => GroupChannel | null
  checkIfFileSizeIsTooLarge: (file: File) => boolean
}

const SendbirdContext = createContext<SendbirdContextType | null>(null)

export const SBProvider = ({ children }: { children: React.ReactNode }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [channels, setChannels] = useState<GroupChannel[]>([])
  const [currentChannel, setCurrentChannel] = useState<GroupChannel | null>(
    null
  )
  const [messages, setMessages] = useState<BaseMessage[]>([])
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const { data: sbCredentials } = useSBCredentialsQuery()

  useEffect(() => {
    const connectSendbird = async () => {
      if (!sbCredentials?.sendbird_user_id) return
      if (!sbCredentials?.sendbird_access_token) return
      try {
        const user = await connectToSendbird(
          sbCredentials.sendbird_user_id,
          sbCredentials.sendbird_access_token
        )
        setIsConnected(true)
        setCurrentUser(user)
      } catch (error) {
        console.error(error)
      }
    }

    connectSendbird()

    return () => {
      disconnectFromSendbird()
      setIsConnected(false)
      setCurrentUser(null)
    }
  }, [sbCredentials])

  const refreshChannels = useCallback(async () => {
    if (!isConnected) return
    const channelList = await getGroupChannelList()
    setChannels(channelList)
  }, [isConnected])

  const sendMessage = async (
    channelUrl: string,
    message: string,
    parentMessageId?: number
  ): Promise<any> => {
    setIsSendingMessage(true)
    try {
      const channel = await getGroupChannel(channelUrl)
      const messageParams = {
        message,
        ...(parentMessageId && {
          parentMessageId,
          mentionedMessageTemplate: "Replied to a message",
        }),
      }

      const userMessage = channel
        .sendUserMessage(messageParams)
        .onSucceeded((message) => {
          setMessages((prev) => [...prev, message])
          scrollToBottomOfMessages()
        })

      return userMessage
    } finally {
      setIsSendingMessage(false)
    }
  }

  const checkIfFileSizeIsTooLarge = (file: File) => {
    const isFileImage = (file: any) => file.type.startsWith("image/")
    const isFileVideo = (file: any) => file.type.startsWith("video/")
    const isFilePDF = (file: any) => file.type.startsWith("application/pdf")

    // Restrict file size for images
    if (isFileImage(file) && file.size > 5 * 1024 * 1024) {
      alert("Image size greater than 5 MB")
      return true
    }

    // Restrict file size for videos
    if (isFileVideo(file) && file.size > 25 * 1024 * 1024) {
      alert("Video size greater than 25 MB")
      return true
    }

    // Check if the file is an image or video
    if (!isFileImage(file) && !isFileVideo(file) && !isFilePDF(file)) {
      alert("Not an image or video")
      return true
    }

    return false
  }

  const sendFileMessage = async (
    channelUrl: string,
    file: File
  ): Promise<any> => {
    if (!file) {
      return
    }

    if (checkIfFileSizeIsTooLarge(file)) return

    setIsSendingMessage(true)
    try {
      const channel = await getGroupChannel(channelUrl)

      const fileMessage = channel
        .sendFileMessage({ file })
        .onFailed((error) => {
          console.error("Failed to send file", error)
        })
        .onSucceeded((message) => {
          setMessages((prev) => [...prev, message] as any)
          scrollToBottomOfMessages()
        })
      return fileMessage
    } finally {
      setIsSendingMessage(false)
    }
  }

  const deleteMessage = async (channelUrl: string, message: BaseMessage) => {
    const channel = await getGroupChannel(channelUrl)
    await channel.deleteMessage(message)
    setMessages((prev) =>
      prev.filter((msg) => msg.messageId !== message.messageId)
    )
  }

  const updateMessage = async (
    channelUrl: string,
    messageId: number,
    text: string
  ) => {
    const channel = await getGroupChannel(channelUrl)
    const message = await channel.updateUserMessage(messageId, {
      message: text,
    })

    setMessages((prev) =>
      prev.map((msg) => (msg.messageId === messageId ? message : msg))
    )
    return message
  }

  const loadPreviousMessages = async () => {
    if (!currentChannel || isLoadingMessages || messages.length === 0) return
    setIsLoadingMessages(true)

    try {
      // Create a minimum delay promise
      const minimumDelay = new Promise((resolve) => setTimeout(resolve, 500))

      // Get messages from the API
      const [messageList] = await Promise.all([
        currentChannel.getMessagesByTimestamp(
          messages[0]?.createdAt || Date.now(),
          {
            prevResultSize: 20,
            nextResultSize: 0,
            includeParentMessageInfo: true,
            replyType: ReplyType.ALL,
          }
        ),
        minimumDelay, // This ensures we wait at least 500ms
      ])

      // Don't add messages we already have
      const uniqueMessages = messageList.filter(
        (newMsg) =>
          !messages.some(
            (existingMsg) => existingMsg.messageId === newMsg.messageId
          )
      )

      if (uniqueMessages.length === 0) {
        setHasMore(false)
        return
      }

      setMessages((prev) => [...uniqueMessages, ...prev])
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const markAsRead = async (channelUrl: string) => {
    const channel = await getGroupChannel(channelUrl)
    await channel.markAsRead()
    const newChannels = await getGroupChannelList()
    setChannels(newChannels)
  }

  const getChannelByUrl = (channelUrl: string) => {
    return channels.find((channel) => channel.url === channelUrl) || null
  }

  useEffect(() => {
    if (isConnected) refreshChannels()
  }, [isConnected, refreshChannels])

  useEffect(() => {
    const loadMessages = async () => {
      if (!currentChannel) return setMessages([])

      setIsLoadingMessages(true)
      setHasMore(true)
      try {
        const messageList = await currentChannel.getMessagesByTimestamp(
          Date.now(),
          {
            prevResultSize: 30,
            nextResultSize: 0,
            includeParentMessageInfo: true, // Add this
            replyType: ReplyType.ALL,
            reverse: true,
          }
        )
        setMessages(messageList.reverse())
        scrollToBottomOfMessages("auto")
      } catch (error) {
        console.error("Error loading messages:", error)
      } finally {
        setIsLoadingMessages(false)
      }
    }

    loadMessages()
  }, [currentChannel])

  useEffect(() => {
    if (currentChannel) {
      // Create a handler that extends GroupChannelHandler
      const channelHandler = new GroupChannelHandler({
        onMessageReceived(channel: BaseChannel, message: BaseMessage) {
          if (channel.url === currentChannel.url) {
            setMessages((prev) => [...prev, message])
            scrollToBottomOfMessages()
            markAsRead(channel.url)
          }
          const channelIndex = channels.findIndex(
            (ch) => ch.url === channel.url
          )

          // replace the channel at that index with the new channel
          setChannels((prev) => {
            const newChannels = [...prev]
            newChannels[channelIndex] = channel as GroupChannel
            return newChannels
          })
        },

        onMessageUpdated(channel: BaseChannel, message: BaseMessage) {
          if (channel.url === currentChannel.url) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.messageId === message.messageId ? message : msg
              )
            )
          }
        },

        onMessageDeleted(channel: BaseChannel, messageId: number) {
          if (channel.url === currentChannel.url) {
            setMessages((prev) =>
              prev.filter((msg) => msg.messageId !== messageId)
            )
          }
        },
      })

      // Register the handler with a unique ID
      sb.groupChannel.addGroupChannelHandler(
        `MESSAGE_HANDLER_${currentChannel.url}`,
        channelHandler
      )

      return () => {
        sb.groupChannel.removeGroupChannelHandler(
          `MESSAGE_HANDLER_${currentChannel.url}`
        )
      }
    }
  }, [currentChannel])

  const scrollToBottomOfMessages = (behavior: ScrollBehavior = "smooth") => {
    const messagesContainerEl = "#messages-container"
    const messagesContainer = document.querySelector(messagesContainerEl)

    setTimeout(() => {
      const top = messagesContainer?.scrollHeight
      messagesContainer?.scrollTo({ top, behavior })
    }, 150)
  }

  return (
    <SendbirdContext.Provider
      value={{
        isConnected,
        currentUser,
        channels,
        currentChannel,
        setCurrentChannel,
        refreshChannels,
        messages,
        setMessages,
        sendMessage,
        sendFileMessage,
        deleteMessage,
        updateMessage,
        loadPreviousMessages,
        markAsRead,
        scrollToBottomOfMessages,
        // startTyping,
        // endTyping,
        isLoadingMessages,
        isSendingMessage,
        hasMore,
        getChannelByUrl,
        checkIfFileSizeIsTooLarge,
      }}
    >
      {children}
    </SendbirdContext.Provider>
  )
}

export default SBProvider

export const useSendbird = () => {
  const context = useContext(SendbirdContext)
  if (!context) {
    throw new Error("useSendbird must be used within a SendbirdProvider")
  }
  return context
}
